import React from 'react';
import { H1, LabelUppercase, P, PSmall } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import styled, { useTheme } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useGetCompany } from '../../hooks/use-hydra/admin';
import { prop } from 'styled-tools';
import { FormInput, FormLabel } from '../../components/Form';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';

import Button from '../../components/elements/Button';
import CompanyPageWrapper from '../../components/Admin/CompanyPageWrapper';
import { COMPANY_PAGES } from '../../components/Admin/CompanySideNavigation';
import Checkbox from '../../components/elements/Checkbox';
import { updateCompany } from '../../api/admin';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import useAuth from '../../hooks/use-auth';
import { ROUTES } from '../../routing';
day.extend(utc);

const NavbarWithBreadcrumbs = ({
  name,
  companyId,
}: {
  name?: string;
  companyId: string;
}) => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: ROUTES.HOME.getPath(),
      },
      {
        label: 'Teams',
        url: ROUTES.ADMIN.COMPANIES.getPath(),
      },
      {
        label: name || 'Company',
        url: ROUTES.ADMIN.COMPANY.getPath({ companyId }),
      },
      {
        label: 'Settings',
        url: null,
      },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0px;
  padding-bottom: 125px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled(P)`
  font-weight: 700;
  color: ${prop('theme.colors.textPrimary')};
  margin-top: 16px;
`;

const Label = styled(PSmall)`
  margin-top: 0px;
  padding-bottom: 0px;
`;

const SettingsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
`;

export const CheckboxSetting = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}) => {
  return (
    <Row style={{ alignItems: 'center' }}>
      <Checkbox isChecked={value} onChange={() => onChange(!value)} />
      <Label>{label}</Label>
    </Row>
  );
};

export default function CompanySettings() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useGetCompany(parseInt(id));
  const { getTokenSilently } = useAuth();
  const theme = useTheme();
  // Store original values from the database
  const originalSettings = React.useMemo(
    () => ({
      KICK_OFF: data?.settings?.KICK_OFF ?? false,
      COMPANY_PROJECT_UPLOAD: data?.settings?.COMPANY_PROJECT_UPLOAD ?? false,
      RETRO_SURVEY: data?.settings?.RETRO_SURVEY ?? false,
      WELCOME_EMAIL: data?.settings?.WELCOME_EMAIL ?? false,
      OFFICE_HOURS_REMINDERS: data?.settings?.OFFICE_HOURS_REMINDERS ?? false,
      LEARNING_PLAN_NOTIFICATIONS:
        data?.settings?.LEARNING_PLAN_NOTIFICATIONS ?? false,
      ASSESSMENT_TEST: data?.settings?.ASSESSMENT_TEST ?? false,
      logoUrl: data?.logoUrl ?? null,
    }),
    [data?.settings, data?.logoUrl]
  );

  const [isKickOffEnabled, setIsKickOffEnabled] = React.useState(
    originalSettings.KICK_OFF
  );
  const [isCompanyProjectUploadsEnabled, setIsCompanyProjectUploadsEnabled] =
    React.useState(originalSettings.COMPANY_PROJECT_UPLOAD);
  const [isRetroSurveyEnabled, setIsRetroSurveyEnabled] = React.useState(
    originalSettings.RETRO_SURVEY
  );
  const [isCohortWelcomeEmailEnabled, setIsCohortWelcomeEmailEnabled] =
    React.useState(originalSettings.WELCOME_EMAIL);
  const [isOfficeHoursRemindersEnabled, setIsOfficeHoursRemindersEnabled] =
    React.useState(originalSettings.OFFICE_HOURS_REMINDERS);
  const [
    isLearningPlanNotificationsEnabled,
    setIsLearningPlanNotificationsEnabled,
  ] = React.useState(originalSettings.LEARNING_PLAN_NOTIFICATIONS);
  const [isAssessmentTestEnabled, setIsAssessmentTestEnabled] = React.useState(
    originalSettings.ASSESSMENT_TEST
  );

  const [logoUrl, setLogoUrl] = React.useState(data?.logoUrl ?? null);
  // Update state when data loads
  React.useEffect(() => {
    if (data?.settings) {
      setIsKickOffEnabled(data.settings.KICK_OFF ?? false);
      setIsCompanyProjectUploadsEnabled(
        data.settings.COMPANY_PROJECT_UPLOAD ?? false
      );
      setIsRetroSurveyEnabled(data.settings.RETRO_SURVEY ?? false);
      setIsCohortWelcomeEmailEnabled(data.settings.WELCOME_EMAIL ?? false);
      setIsOfficeHoursRemindersEnabled(
        data.settings.OFFICE_HOURS_REMINDERS ?? false
      );
      setIsLearningPlanNotificationsEnabled(
        data.settings.LEARNING_PLAN_NOTIFICATIONS ?? false
      );
      setIsAssessmentTestEnabled(data.settings.ASSESSMENT_TEST ?? false);
      setLogoUrl(data.logoUrl ?? null);
    }
  }, [data?.settings, data?.logoUrl]);

  // Check if there are any changes
  const hasChanges = React.useMemo(() => {
    return (
      isKickOffEnabled !== originalSettings.KICK_OFF ||
      isCompanyProjectUploadsEnabled !==
        originalSettings.COMPANY_PROJECT_UPLOAD ||
      isRetroSurveyEnabled !== originalSettings.RETRO_SURVEY ||
      isCohortWelcomeEmailEnabled !== originalSettings.WELCOME_EMAIL ||
      isOfficeHoursRemindersEnabled !==
        originalSettings.OFFICE_HOURS_REMINDERS ||
      isLearningPlanNotificationsEnabled !==
        originalSettings.LEARNING_PLAN_NOTIFICATIONS ||
      isAssessmentTestEnabled !== originalSettings.ASSESSMENT_TEST ||
      logoUrl !== originalSettings.logoUrl
    );
  }, [
    isKickOffEnabled,
    isCompanyProjectUploadsEnabled,
    isRetroSurveyEnabled,
    isCohortWelcomeEmailEnabled,
    isOfficeHoursRemindersEnabled,
    originalSettings,
    isLearningPlanNotificationsEnabled,
    isAssessmentTestEnabled,
    logoUrl,
  ]);

  const updateCompanyMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      const response = await updateCompany(token, {
        companyId: parseInt(id),
        isKickOffEnabled,
        isCompanyProjectUploadsEnabled,
        isRetroSurveyEnabled,
        isCohortWelcomeEmailEnabled,
        isOfficeHoursRemindersEnabled,
        isLearningPlanNotificationsEnabled,
        isAssessmentTestEnabled,
        logoUrl: logoUrl?.trim() || null,
      });
      return response;
    },
    onSuccess: async () => {
      toast.success('Company settings updated successfully');
      await refetch();
    },
    onError: (error: Error | AxiosError) => {
      const errorMessage = error?.message;
      toast.error(`Failed to update company settings - ${errorMessage}`);
    },
  });

  const onSave = () => {
    updateCompanyMutation.mutate();
  };

  return (
    <PageWithAsyncData
      isLoading={isLoading}
      error={error}
      retry={refetch}
      noScroll
    >
      <NavbarWithBreadcrumbs name={data?.name} companyId={id} />
      <CompanyPageWrapper currentPage={COMPANY_PAGES.SETTINGS}>
        <H1>{data?.name}</H1>
        <Content>
          <Section>
            <Title>Company Info</Title>
            <Row>
              <Column>
                <FormLabel style={{ marginTop: '0px' }}>Slug</FormLabel>
                <FormInput
                  value={data?.slug ?? ''}
                  isDisabled
                  onChange={() => {}}
                  placeholder="Slug"
                />
              </Column>
              <Column>
                <FormLabel style={{ marginTop: '0px' }}>Created at</FormLabel>
                <FormInput
                  value={day.utc(data?.createdAt).format('DD/MM/YYYY HH:mm')}
                  isDisabled
                  onChange={() => {}}
                  placeholder="Created at"
                />
              </Column>
            </Row>
          </Section>
          <Section>
            <Title>Configuration</Title>
            <Row>
              <SettingsWrapper>
                <CheckboxSetting
                  label="Enable cohort invite onboarding email sequence"
                  value={isCohortWelcomeEmailEnabled}
                  onChange={(value) => {
                    setIsCohortWelcomeEmailEnabled(value);
                  }}
                />
                <CheckboxSetting
                  label="Show kick-off onboarding question & 'Next kickoff' banner on the home page."
                  value={isKickOffEnabled}
                  onChange={(value) => {
                    setIsKickOffEnabled(value);
                  }}
                />
                <CheckboxSetting
                  label="Enable cohort milestones - Check-in #1, Check-in #2, and Project Upload + notification emails"
                  value={isCompanyProjectUploadsEnabled}
                  onChange={(value) => {
                    setIsCompanyProjectUploadsEnabled(value);
                  }}
                />
                <CheckboxSetting
                  label="Enable cohort milestone - Retro Survey + notification emails"
                  value={isRetroSurveyEnabled}
                  onChange={(value) => {
                    setIsRetroSurveyEnabled(value);
                  }}
                />
                <CheckboxSetting
                  label="Enable email reminders 4 hours before office hours"
                  value={isOfficeHoursRemindersEnabled}
                  onChange={(value) => {
                    setIsOfficeHoursRemindersEnabled(value);
                  }}
                />
                <CheckboxSetting
                  label="Enable learning plan notifications"
                  value={isLearningPlanNotificationsEnabled}
                  onChange={(value) => {
                    setIsLearningPlanNotificationsEnabled(value);
                  }}
                />
                <CheckboxSetting
                  label="Enable assessment tests for this company"
                  value={isAssessmentTestEnabled}
                  onChange={(value) => {
                    setIsAssessmentTestEnabled(value);
                  }}
                />
              </SettingsWrapper>
            </Row>
            <Row>
              <Column>
                <FormLabel>Logo</FormLabel>
                <FormInput
                  value={logoUrl ?? ''}
                  onChange={(e) => {
                    setLogoUrl(e.target.value);
                  }}
                  placeholder="Logo"
                />
                <LabelUppercase
                  style={{ marginTop: 4, color: theme.colors.textPlaceholder }}
                >
                  Optional company logo URL
                </LabelUppercase>
              </Column>
            </Row>
          </Section>
          <Section>
            <Row>
              <Button
                label="Save"
                isLoading={updateCompanyMutation.isLoading}
                isDisabled={!hasChanges || updateCompanyMutation.isLoading}
                type="primary"
                showIconBeforeLabel
                faIcon="fa-solid fa-save"
                tooltip={hasChanges ? '' : 'No changes to save'}
                onClick={onSave}
              />
            </Row>
          </Section>
        </Content>
      </CompanyPageWrapper>
    </PageWithAsyncData>
  );
}
